import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Tab } from '@mui/material'
import { useEffect, useState } from 'react'
import useAuth from '../../../hooks/useAuth'
import { CompanyIssues } from './CompanyIssues'
import { CompanyWarrants } from './CompanyWarrants'
import React from 'react'
import { sortAndSetIssuesFunction } from './CompanyTargets'
import IssueApi from '../../../api/IssueApi'
import WarrantApi from '../../../api/WarrantApi'
import ConvertiblesApi from '../../../api/ConvertiblesApi'
import { CompanyConvertibles } from './CompanyConvertibles'
import { IssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { PRInputListItem, SourceType, WarrantListItem } from '@finance-lab/utils/types'
import { CompanyEntity } from 'types/CompanyEntity'
import { TransactionType } from '@finance-lab/utils/types'

const issueApi = new IssueApi()
const warrantApi = new WarrantApi()
const convertibleApi = new ConvertiblesApi()

export const CompanyEvents = ({
    setEntityType,
    company,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
}: {
    company: Pick<CompanyEntity, 'id' | 'name'>
    setEntityType: (t: TransactionType | null) => void
    setSourceType: (s: SourceType | null) => void
    selectedPR: Pick<PRInputListItem, 'id'>
    onArchivePR: (p: Pick<PRInputListItem, 'id'>) => Promise<void>
    sourceType: SourceType | null
}) => {
    const { isAuthenticated } = useAuth()
    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [warrants, setWarrants] = useState<WarrantListItem[] | null>(null)
    const [convertibles, setConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [selectedTab, setSelectedTab] = useState<'issues' | 'warrants' | 'convertibles'>('issues')

    const fetchIssues = React.useCallback(async () => {
        const result = await issueApi.findByCompanyId(company.id, false)
        setIssues(sortAndSetIssuesFunction(result))
    }, [company.id])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        void fetchIssues()
    }, [isAuthenticated, company, setIssues, fetchIssues])

    const fetchWarrants = React.useCallback(async () => {
        const result = await warrantApi.findByCompanyId(company.id)
        // keep only one of each warrant (by id)
        const uniqueWarrants = result.filter((w, i) => result.findIndex((w2) => w2.id === w.id) === i)

        uniqueWarrants.sort((a, b) => {
            // Get the newest subEndDate for warrant `a`
            const s1 =
                a.subscriptionPeriods && a.subscriptionPeriods.length
                    ? Math.max(...a.subscriptionPeriods.map((period) => new Date(period.subEndDate ?? 0).getTime()))
                    : null

            // Get the newest subEndDate for warrant `b`
            const s2 =
                b.subscriptionPeriods && b.subscriptionPeriods.length
                    ? Math.max(...b.subscriptionPeriods.map((period) => new Date(period.subEndDate ?? 0).getTime()))
                    : null

            // Sort by newest subEndDate first
            if (s1 === null && s2 === null) return 0 // Both are null, they are equal
            if (s1 === null) return 1 // `a` has no date, `b` should come first
            if (s2 === null) return -1 // `b` has no date, `a` should come first

            return s2 - s1 // Newest dates first
        })

        setWarrants(uniqueWarrants)
    }, [company.id])

    useEffect(() => {
        if (!isAuthenticated) return
        void fetchWarrants()
    }, [isAuthenticated, fetchWarrants])

    const fetchConvertibles = React.useCallback(async () => {
        const result = await convertibleApi.findByCompanyId(company.id)
        setConvertibles(result)
    }, [company.id])

    useEffect(() => {
        if (!isAuthenticated) return

        void fetchConvertibles()
    }, [isAuthenticated, fetchConvertibles])

    if (company === null) return null

    const handleTabChange = (newValue: 'issues' | 'warrants' | 'convertibles') => {
        setSelectedTab(newValue)
        if (newValue === 'convertibles') {
            setEntityType('Convertible')
        } else {
            setEntityType(null)
        }
    }

    // return content
    return (
        <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={(_e, newValue) => handleTabChange(newValue)}>
                    <Tab label='Emissioner' value='issues' />
                    <Tab label='Teckningsoptioner' value='warrants' />
                    <Tab label='Konvertibler' value='convertibles' />
                </TabList>
            </Box>
            <TabPanel value='issues'>
                <CompanyIssues
                    company={company}
                    issues={issues}
                    onReloadIssues={fetchIssues}
                    sourceType={sourceType}
                    setSourceType={setSourceType}
                    selectedPR={selectedPR}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
            <TabPanel value='warrants'>
                <CompanyWarrants
                    warrants={warrants}
                    sourceType={sourceType}
                    selectedPR={selectedPR}
                    setSourceType={setSourceType}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
            <TabPanel value='convertibles'>
                <CompanyConvertibles
                    company={company}
                    onReloadConvertibles={fetchConvertibles}
                    convertibles={convertibles}
                    sourceType={sourceType}
                    setSourceType={setSourceType}
                    selectedPR={selectedPR}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
        </TabContext>
    )
}
